/**
 * SSR 미들웨어 전용 in-process LRU + TTL 캐시 + single-flight dedupe.
 *
 * 목적: AI 크롤러 버스트 등 동일 상품 반복 SSR 요청 시 백엔드 API 호출을 흡수하여
 *       백엔드 부하가 트래픽에 선형 비례하는 것을 방지 (캐시 레이어가 전무했던 게 실질 위험).
 *
 * 특성:
 * - Map(삽입 순서 = LRU 순서) 기반. get 시 최근 사용 위치로 이동, 용량 상한 초과 시 최저 사용 항목 제거.
 * - 프로세스 단위 캐시 (다중 워커면 각각 독립 캐시 — 비례적 부하 감소 효과는 유지).
 * - 값 갱신 주기가 느린 정적 데이터(고시정보/상세설명)에 장시간 TTL, 휘발성 데이터(가격/재고)에 단시간 TTL 부여 권장.
 * - 실패/예외 응답은 캐시하지 않도록 설계 (load는 loader throw 시 캐시하지 않음).
 * - single-flight(load): 캐시 미스 시 동일 key로 동시에 진행 중인 loader 호출을 1회로 병합 →
 *   TTL 만료 직후 동시 요청이 백엔드를 동시에 치는 스탬피드(thundering herd) 방지.
 *
 * 메트릭:
 * - hits/dedupes/misses/loads/errors + loader 누적 지연을 카운트. hitRate·avgLoadMs 계산.
 * - getMetrics()/resetMetrics()로 조회/리셋 가능.
 * - 환경변수 SSR_CACHE_METRICS_INTERVAL_MS(밀리초) 설정 시 해당 주기로 요약 1줄 로깅 (.unref).
 *   기본 0=비활성화 → 측정이 필요한 스테이징/트러블슈팅 시에만 켜서 운영 로그/부하 영향 최소화.
 */
const CACHE = new Map()
const MAX_ENTRIES = 2000

// single-flight: key → 진행 중 Promise. 동시 요청 병합으로 스탬피드 방지.
const INFLIGHT = new Map()

// 메트릭 카운터 (프로세스 단위). getMetrics()로 조회, resetMetrics()로 리셋.
const stats = {
  hits: 0, // CACHE 히트 (백엔드 호출 0)
  dedupes: 0, // INFLIGHT 재사용 (single-flight로 병합된 동시 요청)
  misses: 0, // 신규 loader 시작 = 백엔드 호출 시도
  loads: 0, // loader 성공 완료
  errors: 0, // loader 실패 (throw)
  loadMs: 0 // loader 누적 소요 시간 (ms)
}

function get (key) {
  const entry = CACHE.get(key)
  if (!entry) { return undefined }
  if (Date.now() > entry.expires) {
    CACHE.delete(key)
    return undefined
  }
  // LRU 갱신: 최근 사용 위치로 이동
  CACHE.delete(key)
  CACHE.set(key, entry)
  return entry.value
}

function set (key, value, ttlMs) {
  if (CACHE.size >= MAX_ENTRIES) {
    const oldest = CACHE.keys().next().value
    if (oldest !== undefined) { CACHE.delete(oldest) }
  }
  CACHE.set(key, { value, expires: Date.now() + ttlMs })
}

/**
 * 캐시 조회 + single-flight 로드를 통합.
 * - 캐시 히트: 저장된 값 즉시 반환 (hits++).
 * - 캐시 미스 + 동일 key 진행 중: 기존 Promise 재사용 (dedupes++, 동시 N→1 호출 병합).
 * - 캐시 미스 + 신규: loader() 호출 (misses++). 성공 시 캐시 저장(loads++), throw 시 캐시 안 함(errors++).
 *
 * @param {string} key
 * @param {number} ttlMs - 캐시 수명 (밀리초)
 * @param {() => Promise<*>} loader - 캐시 미스 시 호출할 비동기 팩토리
 * @returns {Promise<*>} 캐시된 값 또는 loader 결과
 */
export function load (key, ttlMs, loader) {
  const hit = get(key)
  if (hit !== undefined) {
    stats.hits++
    return Promise.resolve(hit)
  }
  // single-flight: 진행 중 동일 key 요청은 새 팩토리 호출 없이 기존 Promise 재사용
  const pending = INFLIGHT.get(key)
  if (pending) {
    stats.dedupes++
    return pending
  }
  stats.misses++
  const started = Date.now()
  const p = (async () => {
    try {
      const value = await loader()
      set(key, value, ttlMs)
      stats.loads++
      stats.loadMs += Date.now() - started
      return value
    } catch (e) {
      stats.errors++
      stats.loadMs += Date.now() - started
      throw e
    } finally {
      // 성공/실패 무관 진행 중 맵에서 제거 (실패값은 캐시에 들어가지 않음)
      INFLIGHT.delete(key)
    }
  })()
  INFLIGHT.set(key, p)
  return p
}

/**
 * 누적 메트릭 스냅샷.
 * @returns {{hits:number,dedupes:number,misses:number,loads:number,errors:number,loadMs:number,total:number,hitRate:number,avgLoadMs:number,entries:number,inflight:number}}
 */
export function getMetrics () {
  const total = stats.hits + stats.dedupes + stats.misses
  const hitRate = total > 0 ? Number(((stats.hits + stats.dedupes) / total * 100).toFixed(1)) : 0
  const avgLoadMs = stats.loads > 0 ? Math.round(stats.loadMs / stats.loads) : 0
  return {
    ...stats,
    total,
    hitRate, // (hits+dedupes)/total — 백엔드 호출을 피한 비율
    avgLoadMs, // loader 1회당 평균 지연 (백엔드 응답 속도 지표)
    entries: CACHE.size,
    inflight: INFLIGHT.size
  }
}

/** 메트릭 카운터 0 리셋 (측정 구간 시작 시 사용). */
export function resetMetrics () {
  stats.hits = 0
  stats.dedupes = 0
  stats.misses = 0
  stats.loads = 0
  stats.errors = 0
  stats.loadMs = 0
}

// 주기 요약 로깅 (옵션). 환경변수로만 활성화 → 기본은 카운터만 누적, 로그/부하 영향 0.
const LOG_INTERVAL_MS = Number(process.env.SSR_CACHE_METRICS_INTERVAL_MS) || 0
if (LOG_INTERVAL_MS > 0 && typeof setInterval === 'function') {
  setInterval(() => {
    const m = getMetrics()
    // eslint-disable-next-line no-console
    console.log(
      `[ssrCache] hits=${m.hits} dedupes=${m.dedupes} misses=${m.misses} loads=${m.loads} errors=${m.errors}` +
      ` | hitRate=${m.hitRate}% avgLoadMs=${m.avgLoadMs} | entries=${m.entries} inflight=${m.inflight}`
    )
  }, LOG_INTERVAL_MS).unref()
}
